import {
  complement,
  contains,
  countBy,
  equals,
  find,
  findIndex,
  flatten,
  isEmpty,
  last,
  lensProp,
  propEq,
  reduce,
  reject,
  set,
  toPairs,
  union,
  update,
  values,
  without,
} from "ramda";

function mostCommonBy<U>(byFunc: (entry: U) => string, entries: U[]): string {
  const counted = countBy(byFunc, entries);
  const mostCommon = reduce<[string, number], [string, number]>(
    (acc, pair) => (pair[1] > acc[1] ? pair : acc),
    ["", 0],
    toPairs(counted)
  );
  return mostCommon[0];
}

function replaceInCollection<T>(oldItem: T, newItem: T, collection: T[]) {
  const existingIdx = findIndex(equals(oldItem), collection);
  if (existingIdx !== -1) {
    return update(existingIdx, newItem, collection);
  }
  return collection.concat(newItem);
}

function toggleListItem<T>(item: T, list: T[]) {
  if (contains(item, list)) {
    return without([item], list);
  } else {
    return union(list, [item]);
  }
}

function commaJoin(strings: string[]): string {
  if (strings.length === 2) {
    return strings.join(" and ");
  }
  if (strings.length > 1) {
    const withCommas = strings.slice(0, -1).join(", ");
    return withCommas + ", and " + last(strings);
  }
  return last(strings) || "";
}

function arrayToIntKeyedHash<T>(arr: T[]): { [index: string]: T } {
  return arr.reduce(
    (acc, item, idx) => set(lensProp(String(idx)), item, acc),
    {}
  );
}

function flattenCollection<T>(collection: T[] | IKeyedGroups<T> | T[][]): T[] {
  if (Array.isArray(collection)) {
    return flatten<T>(collection);
  } else {
    return flatten<T>(values(collection));
  }
}

const includesByLowerCase = (full: string, query: string) =>
  full.toLocaleLowerCase().includes(query.toLocaleLowerCase());

const findById = <T>(id: string, list: T[]) => find<T>(propEq("id", id), list);

const plural = (singleForm: string, length: number, pluralForm?: string) => {
  return length === 1 ? singleForm : pluralForm || `${singleForm}s`;
};

const notEmpty = complement(isEmpty);
const rejectEmpty = reject(isEmpty);

interface IKeyedGroups<T> {
  [index: string]: T[];
}

export {
  arrayToIntKeyedHash,
  commaJoin,
  findById,
  flattenCollection,
  IKeyedGroups,
  includesByLowerCase,
  mostCommonBy,
  notEmpty,
  plural,
  rejectEmpty,
  replaceInCollection,
  toggleListItem,
};
